import {
  standardLearnosityConfig,
  gameLearnosityConfig,
  profilerLearnosityConfig,
  genericLearnosityConfig,
  quizLearnosityConfig,
  learnLearnosityConfig,
  trackerLearnosityConfig,
} from "./learnosity-templates";

export {
  standardLearnosityConfig,
  gameLearnosityConfig,
  profilerLearnosityConfig,
  genericLearnosityConfig,
  quizLearnosityConfig,
  learnLearnosityConfig,
  trackerLearnosityConfig,
};

// Ladder type arrays

export const ladderTypes = [
  "informational",
  "knowledge-builder",
  "skill-builder",
  "profiler-values",
  "profiler-riasec",
  "tracker",
  "generic",
  "learn",
];

export const scoredLadderTypes = ["knowledge-builder", "skill-builder", "quiz"];

export const profilerLadderTypes = [
  "profiler-values",
  "profiler-riasec",
  "inventory",
];

export const externalLadderTypes = ["ibm-skillsbuild"];

// Type checking functions

export const isGenericLadderType = (
  ladderType: string | undefined,
): boolean => {
  return ladderType === "generic";
};

export const isGameLadderType = (ladderType: string | undefined): boolean => {
  return ladderType === "game";
};

export const isExternalLadderType = (
  ladderType: string | undefined,
): boolean => {
  return !!ladderType && externalLadderTypes.includes(ladderType);
};

export const isTrackerLadderType = (
  ladderType: string | undefined,
): boolean => {
  return ladderType === "tracker";
};

export const isRiasecProfilerLadderType = (
  ladderType: string | undefined,
): boolean => {
  return ladderType === "profiler-riasec";
};

export const isValuesProfilerLadderType = (
  ladderType: string | undefined,
): boolean => {
  return ladderType === "profiler-values";
};

export const isProfilerLadderType = (
  ladderType: string | undefined,
): boolean => {
  return !!ladderType && profilerLadderTypes.includes(ladderType);
};

export const isQuizLadderType = (ladderType: string | undefined): boolean => {
  return ladderType === "quiz";
};

export const isLearnLadderType = (ladderType: string | undefined): boolean => {
  return ladderType === "learn";
};

export const isScoredLadderType = (ladderType: string | undefined): boolean => {
  return !!ladderType && scoredLadderTypes.includes(ladderType);
};

export const getLearnosityConfig = (
  ladderType: string | undefined,
): Record<string, unknown> => {
  if (isGenericLadderType(ladderType)) {
    return genericLearnosityConfig;
  }
  if (isGameLadderType(ladderType)) {
    return gameLearnosityConfig;
  }
  if (isProfilerLadderType(ladderType)) {
    return profilerLearnosityConfig;
  }
  if (isTrackerLadderType(ladderType)) {
    return trackerLearnosityConfig;
  }
  if (isQuizLadderType(ladderType)) {
    return quizLearnosityConfig;
  }
  if (isLearnLadderType(ladderType)) {
    return learnLearnosityConfig;
  }
  return standardLearnosityConfig;
};

// Game scoring constants

export const GAME_MAX_SCORE = 8;
export const GAME_PASSING_SCORE = 6;

/**
 * Extract game scores from Learnosity responses.
 * Game questions store score in response.value.score rather than
 * Learnosity's built-in scoring. Returns augmented scores object.
 */
export const extractGameScores = (
  scores: Record<string, { score: number; max_score: number }>,
  responses: Record<string, unknown>,
): Record<string, { score: number; max_score: number }> => {
  const augmented = { ...scores };
  for (const [qid, response] of Object.entries(responses)) {
    const value = (response as { value?: unknown })?.value;
    if (
      typeof value === "object" &&
      value !== null &&
      "score" in value &&
      typeof (value as { score: unknown }).score === "number"
    ) {
      augmented[qid] = {
        score: (value as { score: number }).score,
        max_score: GAME_MAX_SCORE,
      };
    }
  }
  return augmented;
};

// Scoring functions

export const getTotalMaxScore = (
  scores: Record<string, { score: number; max_score: number }>,
): number => {
  return Object.values(scores).reduce((sum, item) => sum + item.max_score, 0);
};

export const getPointsScored = (
  scores: Record<string, { score: number; max_score: number }>,
): number => {
  return Object.values(scores).reduce((sum, item) => sum + item.score, 0);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getReportingFields = (itemsApp: any): Record<string, unknown> => {
  const tags = itemsApp.getTags();
  const items = itemsApp.getItems();
  const responses = itemsApp.getResponses();

  const result: Record<string, unknown> = {};

  Object.entries(tags).forEach(([itemId, itemTags]: [string, unknown]) => {
    (itemTags as string[]).forEach((tag: string) => {
      if (tag.startsWith("ReportMe:")) {
        const reportKey = tag.substring("ReportMe:".length);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const item = (items as any)[itemId];
        if (item && item.response_ids && item.response_ids.length > 0) {
          const firstResponseId = item.response_ids[0];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response = (responses as any)[firstResponseId];
          if (response && response.value) {
            const value = Array.isArray(response.value)
              ? response.value[0]
              : response.value;
            result[reportKey] = value;
          }
        }
      }
    });
  });

  return result;
};
